import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, mediaSrc, hasRichText, rewriteHtmlMedia } from "../api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/Logo";
import { LevelChip } from "../components/ui";
import { ArrowLeft, Flag, Lightbulb, CheckCircle2, Terminal, BookOpen, Paperclip, Download } from "lucide-react";

export default function CTFDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [c, setC] = useState(null);
  const [flag, setFlag] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/ctf/challenges/${cid}`).then((r) => setC(r.data.challenge)).catch(() => {});
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <Spinner />;

  const getHint = async () => {
    try {
      const { data } = await api.post(`/ctf/challenges/${cid}/hint`);
      toast.message("Hint unlocked", { description: `Solve reward reduced by ${data.hint_cost} XP` });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/ctf/challenges/${cid}/submit`, { flag });
      if (data.correct) {
        toast.success(data.already_solved ? "Already solved!" : `Correct! +${data.xp_awarded} XP 🎉`);
        await refresh();
        load();
        setFlag("");
      } else {
        toast.error("Incorrect flag. Keep trying!");
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="ctf-detail">
      <button className="btn-ghost text-sm mb-4 -ml-2" onClick={() => navigate("/app/ctf")}><ArrowLeft size={16} /> Back to Arena</button>

      <div className="flex items-center gap-2 mb-3">
        <span className="chip bg-white/5 text-slate-300">{c.category}</span>
        <LevelChip level={c.level} />
        {c.solved && <span className="chip bg-emerald-500/15 text-emerald-400"><CheckCircle2 size={13} /> Solved</span>}
        <span className="chip bg-[#F26522]/10 text-[#F26522] font-mono">+{c.xp_reward} XP</span>
      </div>
      <h1 className="font-heading text-3xl font-bold" data-testid="ctf-title">{c.title}</h1>

      {hasRichText(c.briefing) && (
        <div className="card p-6 mt-6 scanlines">
          <div className="flex items-center gap-2 text-[#38bdf8] mb-3 font-mono text-sm"><Terminal size={16} /> BRIEFING</div>
          <div className="rich-content" dangerouslySetInnerHTML={{ __html: rewriteHtmlMedia(c.briefing) }} />
        </div>
      )}

      {c.attachments?.length > 0 && (
        <div className="card p-5 mt-5">
          <div className="flex items-center gap-2 overline mb-3"><Paperclip size={14} /> Challenge files</div>
          <div className="space-y-2">
            {c.attachments.map((a, i) => (
              <a key={i} href={mediaSrc(a.url) + "?download=1"} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 bg-[#0B1120] border border-white/5 rounded-lg px-4 py-2.5 hover:border-[#0ea5e9]/40 transition-colors" data-testid={`ctf-attachment-${i}`}>
                <Download size={16} className="text-[#38bdf8] shrink-0" />
                <span className="flex-1 truncate text-sm">{a.filename}</span>
                <span className="text-xs text-slate-500">{Math.round((a.size_bytes || 0) / 1024)} KB</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Hint */}
      <div className="card p-5 mt-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-300"><Lightbulb size={18} className="text-amber-400" /> Hint</div>
          {!c.hint_unlocked && !c.solved && (
            <button className="btn-secondary text-sm" onClick={getHint} data-testid="ctf-hint-btn">Unlock (−{c.hint_cost} XP)</button>
          )}
        </div>
        {(c.hint_unlocked || c.solved) && c.hint_text && <p className="text-slate-300 text-sm mt-3 italic">{c.hint_text}</p>}
      </div>

      {/* Submit */}
      <form onSubmit={submit} className="card p-6 mt-5">
        <label className="label flex items-center gap-2"><Flag size={15} className="text-[#F26522]" /> Submit flag</label>
        <div className="flex gap-3">
          <input className="input font-mono" placeholder="flag{...}" value={flag} onChange={(e) => setFlag(e.target.value)} required data-testid="ctf-flag-input" />
          <button className="btn-primary shrink-0" disabled={busy} data-testid="ctf-submit-flag">Submit</button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Attempts so far: {c.attempts} · Flags are case-sensitive</p>
      </form>

      {/* Walkthrough */}
      {c.solved && c.walkthrough && (
        <div className="card p-6 mt-5 border-l-2 border-emerald-500">
          <div className="flex items-center gap-2 overline mb-2 text-emerald-400"><BookOpen size={14} /> Walkthrough</div>
          <p className="text-slate-300 text-sm leading-relaxed">{c.walkthrough}</p>
        </div>
      )}
    </div>
  );
}
